import React from "react";
import { Link } from "react-router-dom";
import { Shield, ArrowLeft } from "lucide-react";

const EFFECTIVE_DATE = "February 1, 2026";
const COMPANY = "Fractional Risk Group LLC";
const PRODUCT = "RiskSurvey.ai";
const CONTACT_EMAIL = "bill.raab@fractionalriskgroup.com";
const CONTACT_ADDRESS = "Fractional Risk Group LLC, Pennsylvania, USA";

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="w-7 h-7 bg-navy rounded-md flex items-center justify-center">
              <Shield className="w-4 h-4 text-white" />
            </div>
            <span className="font-heading font-bold text-navy text-base">{PRODUCT}</span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-navy transition-colors duration-150"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 md:p-12">

          <div className="mb-10">
            <h1 className="font-heading font-bold text-3xl text-navy mb-2">Terms of Service</h1>
            <p className="text-sm text-slate-500">Effective Date: {EFFECTIVE_DATE}</p>
          </div>

          <div className="prose prose-slate max-w-none space-y-8 text-sm leading-relaxed text-slate-700">

            <section>
              <p>
                These Terms of Service ("Terms") govern your access to and use of {PRODUCT}, a SaaS platform operated by {COMPANY} ("Company," "we," "us," or "our"). By accessing or using the platform, you agree to be bound by these Terms. If you do not agree, do not use the platform.
              </p>
            </section>

            <Section title="1. Description of Service">
              <p>
                {PRODUCT} is an AI-powered loss control survey platform that enables insurance carriers ("Carriers") to conduct virtual property and risk inspections through a chat-based interface with photo upload capability. The platform generates structured findings, risk scores, and professional PDF reports for use in the insurance underwriting and loss control process.
              </p>
              <p className="mt-3">
                The platform operates in two modes: (a) Mode 1 — AI-guided conversational inspection, and (b) Mode 2 — structured survey form. Both modes produce AI-generated findings that are subject to human review before finalization.
              </p>
            </Section>

            <Section title="2. Accounts and Access">
              <ul className="list-disc pl-5 space-y-2">
                <li><strong>Carrier Accounts:</strong> Insurance companies and their authorized staff ("Carrier Users") access the platform through registered carrier accounts. Carrier accounts are subject to approval by {COMPANY}.</li>
                <li><strong>Insured Access:</strong> Policyholders and insureds ("Insured Users") access the platform via unique, time-limited survey links issued by a Carrier. Insured Users do not create accounts.</li>
                <li><strong>Account Security:</strong> You are responsible for maintaining the confidentiality of your credentials. You must notify us immediately of any unauthorized access at {CONTACT_EMAIL}.</li>
                <li><strong>Accuracy:</strong> You agree to provide accurate, current, and complete information when registering or completing a survey.</li>
              </ul>
            </Section>

            <Section title="3. Acceptable Use">
              <p>You agree not to:</p>
              <ul className="list-disc pl-5 mt-2 space-y-2">
                <li>Use the platform for any unlawful purpose or in violation of any applicable laws or regulations.</li>
                <li>Submit false, misleading, or fraudulent information in any inspection or survey.</li>
                <li>Attempt to reverse-engineer, decompile, or extract the underlying AI models, prompts, or algorithms.</li>
                <li>Scrape, crawl, or otherwise extract data from the platform in bulk without written permission.</li>
                <li>Interfere with or disrupt the platform's infrastructure, security, or integrity.</li>
                <li>Use the platform to process information unrelated to loss control and risk assessment.</li>
                <li>Resell or sublicense access to the platform without written authorization from {COMPANY}.</li>
              </ul>
            </Section>

            <Section title="4. AI-Generated Content and Human Review Requirement">
              <p>
                The platform uses artificial intelligence to analyze survey responses and photos and to generate findings, risk scores, and reports. <strong>AI-generated findings are not final and must be reviewed, edited, approved, or dismissed by a qualified loss control professional before being relied upon for underwriting, coverage, or risk management decisions.</strong>
              </p>
              <p className="mt-3">
                {COMPANY} does not warrant that AI-generated findings are accurate, complete, or free of error. All findings labeled "Critical" or "High" severity are automatically flagged for mandatory human review within the platform.
              </p>
              <p className="mt-3">
                Carriers are solely responsible for ensuring that all finalized reports are reviewed by appropriately licensed and qualified personnel before use.
              </p>
            </Section>

            <Section title="5. Intellectual Property">
              <ul className="list-disc pl-5 space-y-2">
                <li><strong>Platform IP:</strong> The {PRODUCT} platform, including its software, AI models, design, and documentation, is the exclusive intellectual property of {COMPANY}. All rights reserved.</li>
                <li><strong>Carrier Content:</strong> Carriers retain ownership of their uploaded guidelines, logos, and inspection data. By uploading content, you grant {COMPANY} a limited license to use such content solely to operate and improve the platform.</li>
                <li><strong>Survey Data:</strong> Inspection data, photos, and reports belong to the Carrier that ordered the inspection. {COMPANY} may use aggregated, de-identified data for product improvement and analytics.</li>
              </ul>
            </Section>

            <Section title="6. Fees and Payment">
              <p>
                Access to {PRODUCT} is subject to a subscription fee and/or per-survey fees as specified in your Order Form or carrier agreement. All fees are due as set forth in the applicable agreement. Failure to pay may result in suspension of access. All fees are non-refundable except as expressly stated in your agreement.
              </p>
            </Section>

            <Section title="7. Data Privacy and Security">
              <p>
                {COMPANY} processes personal data in accordance with our <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link>, which is incorporated into these Terms by reference. We implement industry-standard technical and organizational security measures to protect your data.
              </p>
              <p className="mt-3">
                Carriers are responsible for obtaining all necessary consents from Insured Users prior to initiating an inspection and for complying with all applicable privacy and data protection laws, including state insurance department regulations.
              </p>
            </Section>

            <Section title="8. Confidentiality">
              <p>
                Each party agrees to keep confidential any non-public information received from the other party in connection with the platform ("Confidential Information") and not to disclose such information to third parties without the disclosing party's prior written consent, except as required by law.
              </p>
            </Section>

            <Section title="9. Disclaimers">
              <p>
                THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT. {COMPANY.toUpperCase()} DOES NOT WARRANT THAT THE PLATFORM WILL BE ERROR-FREE, UNINTERRUPTED, OR THAT AI-GENERATED FINDINGS WILL BE ACCURATE OR COMPLETE.
              </p>
              <p className="mt-3">
                THE PLATFORM IS A DECISION-SUPPORT TOOL. IT DOES NOT REPLACE THE PROFESSIONAL JUDGMENT OF LICENSED LOSS CONTROL ENGINEERS, UNDERWRITERS, OR OTHER QUALIFIED INSURANCE PROFESSIONALS.
              </p>
            </Section>

            <Section title="10. Limitation of Liability">
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT WILL {COMPANY.toUpperCase()} BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, DATA, REVENUE, OR BUSINESS OPPORTUNITY, ARISING OUT OF OR IN CONNECTION WITH THESE TERMS OR YOUR USE OF THE PLATFORM, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
              </p>
              <p className="mt-3">
                {COMPANY.toUpperCase()}'S TOTAL AGGREGATE LIABILITY FOR ANY CLAIM ARISING UNDER THESE TERMS WILL NOT EXCEED THE FEES PAID BY YOU IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
              </p>
            </Section>

            <Section title="11. Indemnification">
              <p>
                You agree to indemnify and hold harmless {COMPANY} and its officers, directors, employees, and agents from any claims, damages, losses, liabilities, costs, and expenses (including reasonable attorneys' fees) arising out of or relating to (a) your use of the platform, (b) your violation of these Terms, (c) your violation of any applicable law, or (d) any claim by an Insured User or third party arising from an inspection you ordered or a report you finalized.
              </p>
            </Section>

            <Section title="12. Term and Termination">
              <p>
                These Terms remain in effect for the duration of your use of the platform. Either party may terminate for cause upon written notice if the other party materially breaches these Terms and fails to cure within thirty (30) days of written notice. {COMPANY} may suspend or terminate access immediately for violations of Section 3 (Acceptable Use) or non-payment.
              </p>
              <p className="mt-3">
                Upon termination, Carrier Users may request an export of their inspection data for up to thirty (30) days. Thereafter, data may be deleted in accordance with our data retention policy.
              </p>
            </Section>

            <Section title="13. Governing Law and Dispute Resolution">
              <p>
                These Terms are governed by the laws of the Commonwealth of Pennsylvania, USA, without regard to its conflict of law principles. Any dispute arising under these Terms will be resolved through binding arbitration administered by JAMS in accordance with its Streamlined Arbitration Rules, with proceedings conducted in Pennsylvania. Notwithstanding the foregoing, either party may seek injunctive or other equitable relief in any court of competent jurisdiction.
              </p>
            </Section>

            <Section title="14. Changes to These Terms">
              <p>
                We may update these Terms from time to time. We will notify registered Carrier Users of material changes via email or platform notification at least thirty (30) days before the changes take effect. Continued use of the platform after the effective date constitutes acceptance of the updated Terms.
              </p>
            </Section>

            <Section title="15. Contact Us">
              <p>For questions about these Terms, please contact:</p>
              <address className="mt-2 not-italic text-slate-700">
                <strong>{COMPANY}</strong><br />
                Attn: Bill Raab<br />
                {CONTACT_ADDRESS}<br />
                Email: <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-600 hover:underline">{CONTACT_EMAIL}</a><br />
                Phone: 814-592-5824
              </address>
            </Section>

          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="max-w-4xl mx-auto px-6 py-8 flex items-center justify-between text-xs text-slate-400">
        <span>© 2026 {PRODUCT}. All rights reserved.</span>
        <div className="flex gap-4">
          <Link to="/privacy" className="hover:text-navy transition-colors">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-navy transition-colors">Terms of Service</Link>
        </div>
      </footer>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-heading font-bold text-base text-navy mb-3 pb-2 border-b border-slate-100">{title}</h2>
      {children}
    </section>
  );
}
